import React, { ReactElement } from 'react'
import Icon from '@ui-components/icon'
import TriStateCheckbox from 'live/components/ui/TriStateCheckbox'
import { Collapsable, Title, TreeContainer } from './styled'
import { CheckedMap, Data, TreeState } from './types'

const getStateFromTree = (id: string, parents: string[], treeState: TreeState) => {
    let currentNode = treeState
    for (const parentId of parents) {
        currentNode = currentNode[parentId]?.children
    }
    return id in currentNode ? currentNode[id] : ({} as any)
}

export const renderItem = (
    item: Data,
    selectable: boolean,
    checkedMap: CheckedMap,
    onSelectItem: (id: string, value: boolean, parents: string[]) => void,
    renderTitle: any,
    onCollapse: any,
    treeState: TreeState,
    treeView: any,
    key: string,
    parents: string[] = [],
    level: number = 0
) => {
    const { checked, hasSubItemsChecked, collapsed } = getStateFromTree(item.id, parents, treeState)

    return !treeView || treeView[`${level}-${item.id}`] ? (
        <TreeItem
            key={key}
            item={item}
            selectable={selectable}
            value={checked}
            collapsed={collapsed}
            parents={parents}
            renderTitle={renderTitle}
            onSelectItem={onSelectItem}
            onCollapse={onCollapse}
            hasSubItemsChecked={!checked && hasSubItemsChecked}
            treeState={treeState}
            checkedMap={checkedMap}
            treeView={treeView}
            level={level}
        />
    ) : null
}

interface Props {
    item: {
        id: string
        name: string
        description?: string
        children: any
    }
    selectable: boolean
    value: boolean
    parents: string[]
    treeState: TreeState
    checkedMap: CheckedMap
    collapsed: boolean
    hasSubItemsChecked: boolean
    level: number
    treeView: any
    onSelectItem: (id: string, value: boolean, parents: string[]) => void
    renderTitle?: (item: any, level: number) => ReactElement
    onCollapse: (id: string | number, value: boolean, parents: string[]) => void
}

const TreeItem: React.FC<Props> = ({
    item,
    selectable,
    value,
    parents,
    onSelectItem,
    renderTitle,
    onCollapse,
    hasSubItemsChecked,
    treeState,
    treeView,
    checkedMap,
    collapsed,
    level
}) => {
    const { id, name, children } = item
    return (
        <TreeContainer>
            <Collapsable
                title={
                    <Title
                        selectable={selectable}
                        onClick={() => {
                            const newValue = hasSubItemsChecked ? !hasSubItemsChecked : !value
                            onSelectItem(id, newValue, parents)
                        }}
                    >
                        {selectable && (
                            <TriStateCheckbox 
                                type="checkbox"
                                checked={value}
                                indeterminate={hasSubItemsChecked}
                                onChange={() => null}
                            />
                        )}
                        {children ? (
                            <Icon type={'folder'} style={{ marginRight: 5, fontSize: 16, fontWeight: 400 }} />
                        ) : null}
                        {(renderTitle && renderTitle(item, level)) || name}
                    </Title>
                }
                stickyControls={true}
                stickyWhenClosed={true}
                renderControls={() =>
                    children ? (
                        <div
                            onClick={() => {
                                onCollapse(id, !collapsed, parents)
                            }}
                        >
                            <Icon type={'caret-down'} style={{ fontWeight: 600 }} rotate={!collapsed ? 180 : 0} />
                        </div>
                    ) : null
                }
            >
                {!collapsed &&
                    children?.map((item: any) =>
                        renderItem(
                            item,
                            selectable,
                            checkedMap,
                            onSelectItem,
                            renderTitle,
                            onCollapse,
                            treeState,
                            treeView,
                            `${level + 1}-${item.id}`,
                            [...parents, id],
                            level + 1
                        )
                    )}
            </Collapsable>
        </TreeContainer>
    )
}

export default TreeItem
